import type { Time, TimeAdd, TimeUpdate } from "../models/time";
import { type ApiClient, createEntityStore, runStoreAction } from "./shared";

export type ProjecthoursEntity = Time;

export const createProjecthoursStore = (apiClient: ApiClient) => {
	const useStore = createEntityStore<ProjecthoursEntity>();

	const getBetween = async (
		startIso: string,
		endIso: string,
		userId: string
	): Promise<readonly ProjecthoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const items = await apiClient.get<ProjecthoursEntity[]>("/get/times", {
				$filter: `start ge '${startIso}' and start le '${endIso}' and user__id eq '${userId}'`,
			});
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const getByProjectBetween = async (
		projectId: string,
		startIso: string,
		endIso: string
	): Promise<readonly ProjecthoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const items = await apiClient.get<ProjecthoursEntity[]>("/get/times", {
				$filter: `project__id eq '${projectId}' and start ge '${startIso}' and start le '${endIso}' and end ne null`,
			});
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const getFirstProjectTime = async (projectId: string): Promise<ProjecthoursEntity | null> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const items = await apiClient.get<ProjecthoursEntity[]>("/get/times", {
				$filter: `project__id eq '${projectId}' and start ne null and end ne null`,
				$orderby: "start asc",
				$top: 1,
			});
			useStore.getState().upsertMany(items);
			return items[0] ?? null;
		});
	};

	const getLatestRecordings = async (userId: string, take?: number): Promise<readonly ProjecthoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const query: Record<string, string | number> = {
				$skip: 0,
				$orderby: "start desc",
				$filter: `user__id eq '${userId}' and end eq null`,
			};
			if (take !== undefined) {
				query.$top = take;
			}
			const items = await apiClient.get<ProjecthoursEntity[]>("/get/times", query);
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const add = async (entries: readonly TimeAdd[]): Promise<readonly ProjecthoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<ProjecthoursEntity[] | ProjecthoursEntity>("/add/times", entries);
			const items = Array.isArray(response) ? response : [response];
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const update = async (entries: readonly TimeUpdate[]): Promise<readonly ProjecthoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<ProjecthoursEntity[] | ProjecthoursEntity>("/update/times", entries);
			const items = Array.isArray(response) ? response : [response];
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const remove = async (ids: readonly string[]): Promise<void> => {
		const state = useStore.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			await apiClient.delete<unknown>(`/delete/times/${ids.join(";")}`);
			useStore.getState().removeMany(ids);
		});
	};

	const addTag = async (entries: readonly TimeUpdate[]): Promise<readonly ProjecthoursEntity[]> => update(entries);

	const removeTag = async (entries: readonly TimeUpdate[]): Promise<readonly ProjecthoursEntity[]> => update(entries);

	return {
		useStore,
		actions: {
			getBetween,
			getByProjectBetween,
			getFirstProjectTime,
			getLatestRecordings,
			add,
			update,
			delete: remove,
			addTag,
			removeTag,
		},
	};
};
